import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Star, Flame, Trophy } from "lucide-react";
import { getLevelNickname } from "@/utils/levelUtils";

interface UserStats {
  xp: number;
  level: number;
  streak: number;
}

interface StatsCardProps {
  stats: UserStats;
  badgeCount: number;
}

export const StatsCard = ({ stats, badgeCount }: StatsCardProps) => {
  const xpToNextLevel = (stats.level + 1) * 500;
  const currentLevelXP = stats.level * 500;
  const progressInLevel = ((stats.xp - currentLevelXP) / (xpToNextLevel - currentLevelXP)) * 100;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* XP & Level */}
      <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 border-0 shadow-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Star className="w-5 h-5" />
            Level {stats.level}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-white text-sm">
              <span>{stats.xp} XP</span>
              <span>{xpToNextLevel} XP</span>
            </div>
            <Progress value={Math.max(0, Math.min(100, progressInLevel))} className="h-3" />
            <p className="text-white text-xs font-medium">
              {getLevelNickname(stats.level)}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Streak */}
      <Card className="bg-gradient-to-r from-red-500 to-pink-500 border-0 shadow-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Flame className="w-5 h-5" />
            Daily Streak
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">{stats.streak}</div>
            <p className="text-pink-100 text-sm">
              {stats.streak === 0 ? 'Start your streak!' : 'days in a row!'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Badge Count */}
      <Card className="bg-gradient-to-r from-green-500 to-teal-500 border-0 shadow-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Badges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">{badgeCount}</div>
            <p className="text-green-100 text-sm">badges earned</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};